const PERFECT_TIMING_MS_DEFAULT = 45;
const TIMING_FALLOFF_MS = 250;
const W_PRECISION = 0.4;
const W_CONSISTENCY = 0.25;
const W_AGILITY = 0.15;
const W_DURATION = 0.2;
const RECOVERY_TIMEOUT_MS_DEFAULT = 1200;
const CONSISTENCY_WINDOW = 12;
export function timingVerdict(landed, timed, offsetMs, perfectMs = PERFECT_TIMING_MS_DEFAULT) {
    if (!landed)
        return "miss";
    if (!timed)
        return "free";
    const a = Math.abs(offsetMs);
    if (a <= perfectMs)
        return "perfect";
    return offsetMs > 0 ? "early" : "late";
}
export function verdictLabel(v) {
    switch (v) {
        case "perfect":
            return "PERFECT";
        case "early":
            return "EARLY";
        case "late":
            return "LATE";
        case "free":
            return "NICE";
        case "miss":
            return "MISS";
    }
}
export class Scorer {
    constructor() {
        Object.defineProperty(this, "combo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "recovery", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "bestCombo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "basePoints", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 100
        });
        Object.defineProperty(this, "comboCap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 20
        });
        Object.defineProperty(this, "perfectMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: PERFECT_TIMING_MS_DEFAULT
        });
        Object.defineProperty(this, "recoveryTimeoutMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: RECOVERY_TIMEOUT_MS_DEFAULT
        });
        Object.defineProperty(this, "missAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "precisionHistory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    configure(basePoints, comboCap, perfectMs, recoveryTimeoutMs) {
        if (Number.isFinite(basePoints) && basePoints > 0) {
            this.basePoints = basePoints;
        }
        if (Number.isFinite(comboCap) && comboCap > 0) {
            this.comboCap = comboCap;
        }
        if (typeof perfectMs === "number" &&
            Number.isFinite(perfectMs) &&
            perfectMs > 0) {
            this.perfectMs = perfectMs;
        }
        if (typeof recoveryTimeoutMs === "number" &&
            Number.isFinite(recoveryTimeoutMs) &&
            recoveryTimeoutMs > 0) {
            this.recoveryTimeoutMs = recoveryTimeoutMs;
        }
    }
    advance(prev, ev) {
        if (!ev || !ev.delta) {
            console.error("[Scorer] advance called with invalid event", ev);
            return prev;
        }
        if (!Number.isFinite(ev.delta.precision) ||
            !Number.isFinite(ev.delta.consistency)) {
            console.warn("[Scorer] non-finite delta values, coercing to 0", ev.delta);
            ev = {
                ...ev,
                delta: {
                    ...ev.delta,
                    precision: Number.isFinite(ev.delta.precision)
                        ? ev.delta.precision
                        : 0,
                    consistency: Number.isFinite(ev.delta.consistency)
                        ? ev.delta.consistency
                        : 0,
                },
            };
        }
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        if (ev.landed) {
            this.combo += 1;
            if (this.combo > this.bestCombo)
                this.bestCombo = this.combo;
            if (this.missAt !== null) {
                const elapsed = now - this.missAt;
                const MIN_RECOVERY_ELAPSED_MS = 60;
                const credit = elapsed < MIN_RECOVERY_ELAPSED_MS
                    ? 0
                    : clamp01(1 - elapsed / this.recoveryTimeoutMs);
                this.recovery = credit;
                this.missAt = null;
            }
            else {
                this.recovery = 1;
            }
        }
        else {
            this.combo = 0;
            this.missAt = now;
            this.recovery = 0;
        }
        let runConsistency = ev.delta.consistency;
        if (ev.landed) {
            this.precisionHistory.push(ev.delta.precision);
            if (this.precisionHistory.length > CONSISTENCY_WINDOW) {
                this.precisionHistory.shift();
            }
        }
        // Consistency is reported from a rolling window of recent precision as a
        // supplementary HUD stat, but it must NOT override or gate the raw
        // per-event precision that drives scoring. We compute it purely for
        // display alongside the raw signal; the agility/score computation below
        // uses ev.delta.precision (the raw classifier confidence) directly.
        if (this.precisionHistory.length >= 2) {
            const mean = this.precisionHistory.reduce((a, b) => a + b, 0) /
                this.precisionHistory.length;
            let variance = 0;
            for (const p of this.precisionHistory) {
                variance += (p - mean) ** 2;
            }
            variance /= this.precisionHistory.length;
            runConsistency = clamp01(1 - Math.sqrt(variance) * 2);
        }
        const timingScore = ev.timed
            ? Math.max(0, 1 - Math.abs(ev.timingOffsetMs || 0.0) / TIMING_FALLOFF_MS)
            : 1;
        const pitchTerm = typeof ev.pitchScore === "number"
            ? Math.max(0, Math.min(1, ev.pitchScore))
            : null;
        let agility = prev.agility;
        if (ev.landed) {
            const wP = W_PRECISION;
            const wC = W_CONSISTENCY;
            const wD = W_DURATION;
            const wA = ev.timed ? W_AGILITY : 0;
            const collapsed = W_AGILITY - wA;
            const wPrec = wP + collapsed * 0.6;
            const wCons = wC + collapsed * 0.4;
            const durationTerm = pitchTerm !== null ? pitchTerm : ev.delta.precision;
            const raw = wPrec * ev.delta.precision +
                wCons * runConsistency +
                wD * durationTerm +
                wA * timingScore;
            const totalW = wPrec + wCons + wD + wA;
            agility = clamp01(totalW > 0 ? raw / totalW : 0);
        }
        const comboMult = 1 + Math.min(this.combo, this.comboCap) * 0.1;
        const gained = ev.landed
            ? Math.round(agility * this.basePoints * comboMult)
            : 0;
        const verdict = timingVerdict(ev.landed, ev.timed || false, ev.timingOffsetMs || 0.0, this.perfectMs);
        if (ev.landed) {
            console.debug(`[Scorer] LANDED verdict=${verdict} ` +
                `offset=${ev.timingOffsetMs?.toFixed(0)}ms combo=${this.combo} ` +
                `precision=${ev.delta.precision.toFixed(2)} ` +
                `consistency=${runConsistency.toFixed(2)} ` +
                `recovery=${(this.recovery * 100) | 0}% ` +
                `agility=${agility.toFixed(2)} timingScore=${timingScore.toFixed(2)} ` +
                `x${comboMult.toFixed(1)} +${gained} ` +
                `score=${prev.score + gained}`);
        }
        else {
            console.debug(`[Scorer] MISS combo reset recovery=${(this.recovery * 100) | 0}%`);
        }
        return {
            agility,
            combo: this.combo,
            recovery: this.recovery,
            precision: ev.delta.precision,
            score: prev.score + gained,
            consistency: runConsistency,
            lastVerdict: verdict,
            lastGain: gained,
            lastOffsetMs: ev.timed ? ev.timingOffsetMs || 0.0 : 0,
            lastEventAt: typeof performance !== "undefined" ? performance.now() : Date.now(),
        };
    }
    getBestCombo() {
        return this.bestCombo;
    }
    reset() {
        console.debug("[Scorer] reset");
        this.combo = 0;
        this.recovery = 1;
        this.bestCombo = 0;
        this.missAt = null;
        this.precisionHistory = [];
    }
}
function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
